import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import API from "../api/axios";
import "../styles/UserOrders.css";

function UserOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("All");

  const [trackOrder, setTrackOrder] = useState(null);
  const [feedbackOrder, setFeedbackOrder] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* ================= FETCH ORDERS ================= */
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchOrders = async () => {
      try {
        const res = await API.get("/orders/myorders");
        setOrders(res.data.orders || []);
      } catch (err) {
        setError("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, navigate]);

  /* ================= FILTER ================= */
  const filteredOrders = useMemo(() => {
    if (tab === "All") return orders;

    return orders.filter(
      (o) => o.status?.toLowerCase() === tab.toLowerCase()
    );
  }, [orders, tab]);

  /* ================= FEEDBACK ================= */
  const submitFeedback = async () => {
    if (!rating) return alert("Please select rating");
    if (!feedback.trim()) return alert("Please write feedback");

    try {
      setSubmitting(true);

      // 👉 Send review for EACH product in order
      for (const item of feedbackOrder.orderItems) {
        await API.post("/reviews", {
          productId: item.product, // ✅ FIXED
          name: user.name,
          rating,
          comment: feedback,
        });
      }

      alert("Review submitted successfully");

    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
      setFeedbackOrder(null);
      setRating(0);
      setFeedback("");
    }
  };

  /* ================= UI ================= */

  if (loading)
    return <div className="orders-loading">Loading orders...</div>;

  if (error) return <div className="orders-error">{error}</div>;

  return (
    <div className="orders-container">
      <h2 className="orders-title">Your Orders</h2>

      {/* TABS */}
      <div className="orders-tabs">
        {["All", "Pending", "Processing", "Delivered"].map((t) => (
          <button
            key={t}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* EMPTY */}
      {filteredOrders.length === 0 && (
        <div className="no-orders">
          <p>No orders found</p>
          <button onClick={() => navigate("/products")}>
            Start Shopping
          </button>
        </div>
      )}

      {/* ORDERS */}
      {filteredOrders.map((order) => (
        <div key={order._id} className="order-card">

          <div className="order-header">
            <div>
              <span>ORDER ID</span>
              <strong>{order._id}</strong>
            </div>

            <div>
              <span>TOTAL</span>
              <strong>₹{order.totalPrice?.toLocaleString()}</strong>
            </div>

            <div>
              <span>STATUS</span>
              <strong className={`status ${order.status?.toLowerCase()}`}>
                {order.status}
              </strong>
            </div>
          </div>

          {/* PRODUCTS */}
          <div className="order-products">
            {order.orderItems?.map((item) => (
              <div key={item._id} className="order-product">
                <img
                  src={item.image || "/images/placeholder.png"}
                  alt={item.name}
                  onError={(e) =>
                    (e.target.src = "/images/placeholder.png")
                  }
                />

                <div className="product-info">
                  <p className="product-name">{item.name}</p>
                  <p className="product-qty">
                    Qty: {item.qty} • ₹{item.price}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* ACTIONS */}
          <div className="order-actions">
            <button onClick={() => setTrackOrder(order)}>
              Track Order
            </button>

            <button
              className="secondary"
              onClick={() => setFeedbackOrder(order)}
            >
              Leave Feedback
            </button>

            <button
              className="secondary"
              onClick={() => navigate(`/order/${order._id}`)}
            >
              View Details
            </button>
          </div>
        </div>
      ))}

      {/* TRACK MODAL */}
      {trackOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Track Order</h3>

            <ul className="timeline">
              <li className="done">Order Placed</li>

              <li
                className={
                  ["Processing", "Delivered"].includes(trackOrder.status)
                    ? "done"
                    : ""
                }
              >
                Processing
              </li>

              <li
                className={
                  trackOrder.status === "Delivered" ? "done" : ""
                }
              >
                Delivered
              </li>
            </ul>

            <button onClick={() => setTrackOrder(null)}>Close</button>
          </div>
        </div>
      )}

      {/* FEEDBACK MODAL */}
      {feedbackOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Rate Your Order</h3>

            <div className="stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <span
                  key={s}
                  className={rating >= s ? "filled" : ""}
                  onClick={() => setRating(s)}
                >
                  ★
                </span>
              ))}
            </div>

            <textarea
              placeholder="Write your feedback..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />

            <button onClick={submitFeedback} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>

            <button
              className="secondary"
              onClick={() => setFeedbackOrder(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserOrders; 